import {
	ApolloClient,
	ApolloLink,
	InMemoryCache,
	HttpLink,
	Observable,
	from,
	gql,
} from '@apollo/client'
import { onError } from '@apollo/client/link/error'

import { API_BASE_URL_GQL } from '../consts'
import Dialog from '../components/Dialog'
import { getToken, setToken } from './user'

const DEVICE_ID_KEY = 'mitbestellt-device-id'
const REFRESH_TOKEN_KEY = 'mitbestellt-refresh-token'
const USER_ID_KEY = 'mitbestellt-user-id'

// Stable per-browser id, so a dashboard session gets its own refresh-token row in `app`
// instead of kicking the operator's other browsers out on every login.
export const getDeviceId = (): string => {
	const storedDeviceId = localStorage.getItem(DEVICE_ID_KEY)
	if (storedDeviceId) return storedDeviceId
	const deviceId = `web-${crypto.randomUUID()}`
	localStorage.setItem(DEVICE_ID_KEY, deviceId)
	return deviceId
}

export const saveRefreshCredentials = (refreshToken: string, userId: string): void => {
	localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
	localStorage.setItem(USER_ID_KEY, userId)
}

export const getRefreshCredentials = (): { refreshToken: string; userId: string } | null => {
	const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
	const userId = localStorage.getItem(USER_ID_KEY)
	if (!refreshToken || !userId) return null
	return { refreshToken, userId }
}

export const clearRefreshCredentials = (): void => {
	localStorage.removeItem(REFRESH_TOKEN_KEY)
	localStorage.removeItem(USER_ID_KEY)
}

let tokenRefreshPromise: Promise<string | null> | null = null
// Kicked off once at module load (see bottom of file); every other operation waits for it,
// so nothing goes out unauthenticated while the reloaded session is still being restored.
let sessionRestorePromise: Promise<string | null> | null = null

// Trades the stored refresh token for a fresh JWT. The dashboard JWT only lives an hour,
// so this runs both on page load and whenever a request comes back JWT_EXPIRED.
const refreshAuthToken = async (): Promise<string | null> => {
	const credentials = getRefreshCredentials()
	if (!credentials) return null

	try {
		const { data } = await client.mutate({
			mutation: gql`
				mutation RefreshLogin($refreshToken: String!, $userId: Int!) {
					refreshLogin(refreshToken: $refreshToken, userId: $userId) {
						success
						jwt
					}
				}
			`,
			variables: { refreshToken: credentials.refreshToken, userId: Number(credentials.userId) },
			// Skip the error link's retry for this request to avoid infinite loops
			context: { skipRetryAuth: true },
		})
		const jwt = data?.refreshLogin?.jwt
		if (jwt) {
			setToken(jwt)
			return jwt
		}
		clearRefreshCredentials()
		return null
	} catch (e) {
		clearRefreshCredentials()
		return null
	}
}

const httpLink = new HttpLink({
	uri: API_BASE_URL_GQL,
})

const authLink = new ApolloLink((operation, forward) => {
	// The refresh mutation authenticates via the refresh token — never send the expired JWT with it
	if (operation.getContext().skipRetryAuth) return forward(operation)

	return new Observable((observer) => {
		let subscription: { unsubscribe: () => void } | null = null
		const forwardWithToken = async () => {
			await sessionRestorePromise
			operation.setContext({ headers: { Authorization: `Bearer ${getToken()}` } })
			subscription = forward(operation).subscribe({
				next: observer.next.bind(observer),
				error: observer.error.bind(observer),
				complete: observer.complete.bind(observer),
			})
		}
		forwardWithToken()
		return () => subscription?.unsubscribe()
	})
})

const errorLink = onError(({ operation, graphQLErrors, networkError, forward }) => {
	if (graphQLErrors) {
		for (const error of graphQLErrors) {
			console.error(
				`[GraphQL error] ${operation.operationName} (code=${String(error?.extensions?.code)}): ${
					error.message
				}`,
				{ error, variables: operation.variables }
			)
			// The API raises JWT_EXPIRED for an expired token; SESSION_EXPIRED is kept for safety.
			if (error?.extensions?.code === 'JWT_EXPIRED') {
				if (operation.getContext().skipRetryAuth) return
				if (!tokenRefreshPromise) {
					tokenRefreshPromise = (async () => {
						try {
							return await refreshAuthToken()
						} finally {
							tokenRefreshPromise = null
						}
					})()
				}
				const pendingRefresh = tokenRefreshPromise
				return new Observable((observer) => {
					const retryOperation = async () => {
						const newToken = await pendingRefresh
						if (newToken) {
							operation.setContext({ headers: { Authorization: `Bearer ${newToken}` } })
							forward(operation).subscribe({
								next: observer.next.bind(observer),
								error: observer.error.bind(observer),
								complete: observer.complete.bind(observer),
							})
						} else {
							Dialog.render({
								title: 'Sitzung abgelaufen',
								description: 'Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.',
								buttons: [
									{
										id: 'reload',
										label: 'Neu anmelden',
										onClick: () => {
											setToken('')
											clearRefreshCredentials()
											window.location.reload()
										},
										variant: 'mainButton',
									},
								],
								isLocked: true,
							})
						}
					}
					retryOperation()
				})
			}
			// Wie in app2: der Server gibt Titel und Text des Dialogs selbst vor, z. B. bei einer gebannten IP.
			if (error?.extensions?.code === 'GLOBAL_DIALOG') {
				Dialog.render({
					title: (error.extensions.title as string) || 'Hinweis',
					description: (error.extensions.description as string) || error.message,
					isLocked: error.extensions.isLocked === true,
				})
				continue
			}
			if (error?.extensions?.code === 'RATE_LIMITED') {
				Dialog.render({
					title: 'Zu viele Anfragen',
					description:
						error.message || 'Zu viele Anfragen, bitte versuche es in einer Minute erneut.',
				})
				continue
			}
			if (error?.extensions?.code === 'MAINTENANCE_MODE') {
				Dialog.render({
					title: 'Wartungsmodus',
					description:
						'Die Anwendung ist aktuell im Wartungsmodus. Bitte versuche es später erneut.',
					buttons: [
						{
							id: 'reload',
							label: 'Seite neu laden',
							onClick: () => window.location.reload(),
							variant: 'mainButton',
						},
					],
					isLocked: true,
				})
				continue
			}
			if (error?.extensions?.code === 'INTERNAL_SERVER_ERROR') {
				Dialog.render({
					title: 'Unerwarteter Fehler',
					description: 'Ein unerwarteter Fehler ist aufgetreten. Bitte kontaktiere den Support.',
				})
			}
		}
	}
	if (networkError) {
		console.error(`[Network error] ${operation.operationName}:`, networkError)
		Dialog.render({
			title: 'Netzwerkfehler',
			description: 'Es gibt Probleme mit ihrer Internetverbindung.',
		})
	}
})

const client = new ApolloClient({
	cache: new InMemoryCache({
		typePolicies: {
			Restaurant: {
				fields: {
					categories: {
						merge: false,
					},
				},
			},
			Category: {
				fields: {
					products: {
						merge: false,
					},
				},
			},
			Product: {
				fields: {
					variants: {
						merge: false,
					},
				},
			},
			OptionGroup: {
				fields: {
					options: {
						merge: (_existing, incoming) => incoming,
					},
				},
			},
		},
	}),
	link: from([errorLink, authLink, httpLink]),
	defaultOptions: {
		watchQuery: { errorPolicy: 'all', fetchPolicy: 'cache-first' },
		query: { errorPolicy: 'all', fetchPolicy: 'cache-first' },
		mutate: { errorPolicy: 'all' },
	},
})

if (getRefreshCredentials()) {
	sessionRestorePromise = refreshAuthToken()
}

export default client
